import { useState, useEffect } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Loader2, Save, User, Handshake } from "lucide-react";

function parseAddr(s) {
  s = (s || "").trim();
  const out = { via: "", cap: "", comune: "", provincia: "" };
  if (!s) return out;
  const pm = s.match(/\(([A-Za-z]{2})\)\s*$/);
  if (pm) { out.provincia = pm[1].toUpperCase(); s = s.slice(0, pm.index).trim().replace(/,\s*$/, ""); }
  const cm = s.match(/\b(\d{5})\b/);
  if (cm) out.cap = cm[1];
  if (s.includes(",")) {
    const parts = s.split(",").map((x) => x.trim()).filter(Boolean);
    out.via = parts[0] || "";
    if (parts.length > 1) out.comune = (parts[parts.length - 1] || "").replace(/\b\d{5}\b/, "").trim();
  } else {
    out.via = s.replace(/\b\d{5}\b/, "").trim();
  }
  return out;
}

export default function LeadAnagrafica({ lead, onUpdate }) {
  const _rp = (lead.via || lead.comune) ? { via: lead.via || "", comune: lead.comune || "", cap: lead.cap || "", provincia: lead.provincia || "" } : parseAddr(lead.indirizzo_residenza);
  const _ip = (lead.impianto_via || lead.impianto_comune) ? { via: lead.impianto_via || "", comune: lead.impianto_comune || "", cap: lead.impianto_cap || "", provincia: lead.impianto_provincia || "" } : parseAddr(lead.indirizzo_impianto);
  const [f, setF] = useState({
    entity_type: lead.entity_type || "persona_fisica",
    ragione_sociale: lead.ragione_sociale || "",
    partita_iva: lead.partita_iva || "",
    codice_fiscale: lead.codice_fiscale || "",
    nome: lead.nome || "",
    cognome: lead.cognome || "",
    phone: lead.phone || "",
    email: lead.email || "",
    via: _rp.via, comune: _rp.comune, cap: _rp.cap, provincia: _rp.provincia,
    indirizzo_impianto_diverso: !!lead.indirizzo_impianto_diverso,
    impianto_via: _ip.via, impianto_comune: _ip.comune, impianto_cap: _ip.cap, impianto_provincia: _ip.provincia,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const [partners, setPartners] = useState([]);
  const [pcId, setPcId] = useState(lead.partner_commerciale_id || "");
  const [savingPc, setSavingPc] = useState(false);
  useEffect(() => {
    api.get("/users").then((r) => setPartners((r.data || []).filter((u) => u.role === "partner_commerciale"))).catch(() => setPartners([]));
  }, []);
  const savePartner = async (val) => {
    setPcId(val);
    setSavingPc(true);
    try {
      const { data } = await api.put(`/leads/${lead.id}/partner-commerciale`, { partner_commerciale_id: val || null });
      onUpdate(data);
      toast.success(val ? "Partner commerciale collegato" : "Partner commerciale rimosso");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingPc(false); }
  };
  const selectedPartner = partners.find((p) => p.id === pcId);

  const save = async () => {
    if (f.entity_type === "azienda") {
      if (!f.ragione_sociale.trim()) { toast.error("Inserisci la ragione sociale"); return; }
      if (f.partita_iva.trim() && !/^\d{11}$/.test(f.partita_iva.replace(/\s/g, ""))) { toast.error("La partita IVA non è valida (11 cifre)"); return; }
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/leads/${lead.id}`, {
        entity_type: f.entity_type,
        ragione_sociale: f.entity_type === "azienda" ? f.ragione_sociale : "",
        partita_iva: f.entity_type === "azienda" ? f.partita_iva : "",
        codice_fiscale: f.entity_type === "azienda" ? "" : f.codice_fiscale,
        nome: f.nome, cognome: f.cognome, phone: f.phone, email: f.email,
        via: f.via, comune: f.comune, cap: f.cap, provincia: f.provincia,
        indirizzo_impianto_diverso: f.indirizzo_impianto_diverso,
        impianto_via: f.indirizzo_impianto_diverso ? f.impianto_via : "",
        impianto_comune: f.indirizzo_impianto_diverso ? f.impianto_comune : "",
        impianto_cap: f.indirizzo_impianto_diverso ? f.impianto_cap : "",
        impianto_provincia: f.indirizzo_impianto_diverso ? f.impianto_provincia : "",
      });
      onUpdate(data);
      toast.success("Anagrafica salvata");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="rounded border-border p-5" data-testid="anagrafica-card">
      <h2 className="font-heading font-semibold flex items-center gap-2 mb-4"><User className="h-4 w-4 text-primary" strokeWidth={1.5} /> Anagrafica cliente / partner</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Tipo soggetto</Label>
          <Select value={f.entity_type} onValueChange={(v) => set("entity_type", v)}>
            <SelectTrigger data-testid="anagrafica-entity-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="persona_fisica">Persona fisica</SelectItem>
              <SelectItem value="azienda">Azienda</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {f.entity_type === "azienda" ? (
          <>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Ragione sociale *</Label>
              <Input value={f.ragione_sociale} onChange={(e) => set("ragione_sociale", e.target.value)} placeholder="Es. Rossi Impianti Srl" data-testid="anagrafica-ragione-sociale" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Partita IVA</Label>
              <Input value={f.partita_iva} onChange={(e) => set("partita_iva", e.target.value)} placeholder="IT01234567890" data-testid="anagrafica-partita-iva" />
            </div>
          </>
        ) : (
          <div className="sm:col-span-2">
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Codice fiscale <span className="normal-case text-muted-foreground/70">(opzionale)</span></Label>
            <Input value={f.codice_fiscale} onChange={(e) => set("codice_fiscale", e.target.value)} placeholder="RSSMRA80A01H501U" data-testid="anagrafica-codice-fiscale" />
          </div>
        )}
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Nome</Label>
          <Input value={f.nome} onChange={(e) => set("nome", e.target.value)} data-testid="anagrafica-nome" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Cognome</Label>
          <Input value={f.cognome} onChange={(e) => set("cognome", e.target.value)} data-testid="anagrafica-cognome" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Telefono</Label>
          <Input type="tel" value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Es. 333 1234567" data-testid="anagrafica-phone" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Email</Label>
          <Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="cliente@email.it" data-testid="anagrafica-email" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Indirizzo di residenza</Label>
          <div className="grid grid-cols-1 sm:grid-cols-6 gap-2 mt-1">
            <Input className="sm:col-span-4" value={f.via} onChange={(e) => set("via", e.target.value)} placeholder="Via e numero civico" data-testid="anagrafica-via" />
            <Input className="sm:col-span-2" value={f.cap} onChange={(e) => set("cap", e.target.value)} placeholder="CAP" data-testid="anagrafica-cap" />
            <Input className="sm:col-span-4" value={f.comune} onChange={(e) => set("comune", e.target.value)} placeholder="Comune" data-testid="anagrafica-comune" />
            <Input className="sm:col-span-2" value={f.provincia} onChange={(e) => set("provincia", e.target.value.toUpperCase())} placeholder="Prov. (BS)" maxLength={2} data-testid="anagrafica-provincia" />
          </div>
        </div>
        <div className="sm:col-span-2 flex items-center gap-2">
          <Switch checked={f.indirizzo_impianto_diverso} onCheckedChange={(v) => set("indirizzo_impianto_diverso", v)} data-testid="anagrafica-impianto-diverso" />
          <Label className="text-xs text-muted-foreground">Indirizzo impianto diverso dalla residenza</Label>
        </div>
        {f.indirizzo_impianto_diverso && (
          <div className="sm:col-span-2">
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Indirizzo impianto</Label>
            <div className="grid grid-cols-1 sm:grid-cols-6 gap-2 mt-1">
              <Input className="sm:col-span-4" value={f.impianto_via} onChange={(e) => set("impianto_via", e.target.value)} placeholder="Via e numero civico" data-testid="anagrafica-impianto-via" />
              <Input className="sm:col-span-2" value={f.impianto_cap} onChange={(e) => set("impianto_cap", e.target.value)} placeholder="CAP" data-testid="anagrafica-impianto-cap" />
              <Input className="sm:col-span-4" value={f.impianto_comune} onChange={(e) => set("impianto_comune", e.target.value)} placeholder="Comune" data-testid="anagrafica-impianto-comune" />
              <Input className="sm:col-span-2" value={f.impianto_provincia} onChange={(e) => set("impianto_provincia", e.target.value.toUpperCase())} placeholder="Prov. (BS)" maxLength={2} data-testid="anagrafica-impianto-provincia" />
            </div>
          </div>
        )}
      </div>
      <div className="mt-4 rounded-md border border-amber-500/30 bg-amber-500/5 p-3" data-testid="lead-partner-comm-box">
        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><Handshake className="h-3.5 w-3.5 text-amber-600" /> Partner commerciale</Label>
        <p className="text-[11px] text-muted-foreground mt-0.5 mb-2">Collega uno specifico partner: provvigione e marginalità per segmento verranno usate nel preventivo (ricalcolo manuale).</p>
        <div className="flex items-center gap-2">
          <Select value={pcId || "none"} onValueChange={(v) => savePartner(v === "none" ? "" : v)} disabled={savingPc}>
            <SelectTrigger className="h-9" data-testid="lead-partner-comm-select"><SelectValue placeholder="Nessuno" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Nessuno</SelectItem>
              {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {savingPc && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>
        {selectedPartner && (
          <div className="mt-2 text-[11px] text-muted-foreground" data-testid="lead-partner-comm-info">
            Provvigione: <b className="text-foreground">{selectedPartner.commission_pct != null ? `${selectedPartner.commission_pct}%` : "—"}</b>
            {(() => { const seg = lead.target === "commerciale" ? "ci" : lead.target === "industriale" ? "utility" : "residenziale"; const m = (selectedPartner.partner_margini || {})[seg]; return (m != null && m !== "") ? <> · Marginalità ({lead.target || "residenziale"}): <b className="text-foreground">{m}%</b></> : <> · Marginalità: <span className="italic">standard</span></>; })()}
          </div>
        )}
      </div>
      <Button onClick={save} disabled={saving} className="mt-4 font-semibold" data-testid="anagrafica-save">
        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Salva anagrafica
      </Button>
    </Card>
  );
}
